import { useEffect, useRef, useState } from 'react';
import { layout } from '../theme/tokens';

/* MainLayout Content 하단 패딩 — 실측 예약값에 더해 문서 세로스크롤이 생기지 않게 한다.
   상수를 베껴 적지 않고 토큰에서 읽는다 — 두 값이 어긋나면 표 높이 실측이 틀어져
   페이지네이션이 화면 밖으로 밀린다 (2026-09-03 밀도 키트에서 구조적으로 막았다) */
const CONTENT_PADDING_BOTTOM = layout.contentPadding;

/* 파트너 레이아웃은 Content 아래 Footer(저작권 표기)가 있어 그 높이만큼 더 예약해야 한다.
   고정 상수 대신 실측 — 문구/패딩이 바뀌어도 어긋나지 않는다 */
const footerHeight = () =>
  (document.querySelector('.ant-layout-footer') as HTMLElement | null)?.offsetHeight ?? 0;

/**
 * 표 본문 높이를 'calc(100vh - 상수)' 같은 매직넘버 대신 실측으로 정한다.
 * (admin frontend/src/hooks/useViewportFit.ts 와 동일 패턴 — Content 패딩만 24로 다름)
 *
 * - tableWrapRef: 대상 Table 을 감싸는 래퍼 (thead / .ant-table-body 탐색 기준)
 * - cardWrapRef: 페이지 최하단 블록(카드)을 감싸는 래퍼 — 이 요소 bottom 이 뷰포트 안에 들어오게 계산
 * 반환된 bodyHeight 를 Table 의 scroll.y 로 넘긴다.
 */
export function useFitTableHeight(minHeight = 120) {
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const cardWrapRef = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState<number>();

  useEffect(() => {
    const recalc = () => {
      const wrap = tableWrapRef.current;
      const thead = wrap && Array.from(wrap.querySelectorAll('thead')).find(t => (t as HTMLElement).offsetParent !== null);
      if (!wrap || !thead) return;
      const bodyTop = thead.getBoundingClientRect().bottom;      // 본문이 시작되는 실제 y
      const bodyEl = Array.from(wrap.querySelectorAll('.ant-table-body'))
        .find(el => (el as HTMLElement).offsetParent !== null) as HTMLElement | undefined;
      // 본문 아래에 놓인 모든 것(페이지네이션·카드 패딩·테두리)을 실측한다. 기준은 반드시 '카드' —
      // Content 는 minHeight 바닥이 있어 본문이 줄어도 하단이 고정돼 예약값이 커지는 되먹임에 빠진다.
      const reserved = ((bodyEl && cardWrapRef.current)
        ? (cardWrapRef.current.getBoundingClientRect().bottom - bodyEl.getBoundingClientRect().bottom)
          + CONTENT_PADDING_BOTTOM
        : 96) + footerHeight();                                   // : 스크롤 컨테이너 생성 전 최초 1회 근사
      setBodyHeight(prev => {
        const next = Math.max(minHeight, Math.floor(window.innerHeight - bodyTop - reserved));
        const settled = prev !== undefined && Math.abs(prev - next) <= 1 ? prev : next; // 1px 잔떨림 무시
        // 페이지네이션 하단 상시 고정: 본문을 max-height 가 아니라 '고정 높이'로 채워
        // 행이 적어도 pagination 이 뷰포트 바닥에 머문다 (index.css .fit-table 규칙이 변수를 읽음)
        wrap.classList.add('fit-table');
        wrap.style.setProperty('--fit-body-h', `${settled}px`);
        return settled;
      });
    };
    recalc();
    window.addEventListener('resize', recalc);
    /* ── 마감 점검 (자가복구) ──
       페이지네이션이나 상단 요약 카드가 이 계산보다 늦게 들어오면 예약값이 그만큼 모자란 채로
       높이가 굳는다. 그 뒤로는 문서 크기가 더 변하지 않아 ResizeObserver 가 다시 불리지 않아
       스스로 회복하지 못한다 (2026-09-03 시험물질관리에서 약 40% 재현 — 37px 세로스크롤).
       recalc 는 매번 실측만 하므로 늦게라도 한 번 더 돌면 올바른 값으로 수렴한다.
       마운트 직후 몇 초 동안만 돌리고 멈춘다 — 상시 폴링이 아니다. */
    const tick = setInterval(recalc, 500);
    const stop = setTimeout(() => clearInterval(tick), 8000);
    // body 관찰 — 로딩 조기 return 으로 ref 가 늦게 마운트되는 페이지도 잡는다
    const ro = new ResizeObserver(recalc);
    ro.observe(document.body);
    return () => {
      window.removeEventListener('resize', recalc);
      ro.disconnect(); clearInterval(tick); clearTimeout(stop);
    };
  }, [minHeight]);

  return { tableWrapRef, cardWrapRef, bodyHeight };
}

/**
 * 임의 블록을 '여기서부터 뷰포트 바닥까지' 채우는 높이로 실측한다.
 * 표가 아닌 레이아웃(대시보드, 가이드 문서 등)에 쓴다. 반환 height 를 컨테이너 height 로 주고
 * 내부에 overflow:auto 스크롤 영역을 두면 문서 스크롤이 생기지 않는다.
 */
export function useFillHeight(minHeight = 240) {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();

  useEffect(() => {
    const recalc = () => {
      const el = ref.current;
      if (!el || el.offsetParent === null) return;               // 숨김(display:none) 중엔 측정 불가
      const top = el.getBoundingClientRect().top;
      setHeight(prev => {
        const next = Math.max(minHeight, Math.floor(window.innerHeight - top - CONTENT_PADDING_BOTTOM - footerHeight()));
        return prev !== undefined && Math.abs(prev - next) <= 1 ? prev : next;
      });
    };
    recalc();
    window.addEventListener('resize', recalc);
    const ro = new ResizeObserver(recalc);
    ro.observe(document.body);
    return () => { window.removeEventListener('resize', recalc); ro.disconnect(); };
  }, [minHeight]);

  return { ref, height };
}
